import { ChangeDetectionStrategy, Component, OnDestroy, inject, signal } from '@angular/core';
import { ActivatedRoute, ActivatedRouteSnapshot, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { Subscription } from 'rxjs';
import { ClickOutsideDirective } from '../../../shared/directives/click-outside.directive';

export interface ActionSheetConfig {
  /** Desktop/tablet docked-right panel width, e.g. '24rem'. */
  width: string;
  /** Phone-width only: opens already expanded to full height instead of the default medium
   *  ('peek') height. Either way, the mobile sheet always supports the same drag gesture --
   *  up from peek to full, down from full back to peek, and down again from peek to dismiss
   *  -- see mobileState/onDragStart/onDragMove/onDragEnd below. Desktop/tablet ignores this;
   *  it's always the full-height right-docked panel. */
  startFullHeight: boolean;
}

/** Optional hook a routed leaf (AddressDetailComponent, InteractionComponent, ...) can
 *  implement to veto a dismiss -- e.g. a save in flight. Called before an outside click or a
 *  drag-past-threshold closes the sheet. Leaves that don't implement it are always dismissible. */
export interface ActionSheetHost {
  canDismissActionSheet(): boolean;
}

function hasCanDismiss(component: unknown): component is ActionSheetHost {
  return !!component && typeof (component as ActionSheetHost).canDismissActionSheet === 'function';
}

const DEFAULT_CONFIG: ActionSheetConfig = { width: '24rem', startFullHeight: false };

type MobileSheetState = 'peek' | 'full';

/**
 * Shared shell for every /map/addresses/** screen (list, detail, edit, interaction, objection,
 * lead wizard) -- the "docked panel over the live map" chrome (backdrop, positioned panel,
 * mobile bottom-sheet with a 3-state drag gesture, click-outside-to-close) used to be
 * reimplemented (or, for AddressDetailComponent, simply missing) in each of those components
 * individually. This owns it once; the nested <router-outlet> swaps in just the
 * screen-specific content, which picks up sizing via ../../../css/action-sheet-content.css
 * and per-screen shell config via this route's `data.actionSheet` (see app.routes.ts).
 */
@Component({
  selector: 'app-action-sheet',
  standalone: true,
  imports: [RouterOutlet, ClickOutsideDirective],
  templateUrl: './action-sheet.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./action-sheet.component.css', '../../../../css/action-sheet.css'],
})
export class ActionSheetComponent implements OnDestroy {
  private route = inject(ActivatedRoute);
  private router = inject(Router);

  config = signal<ActionSheetConfig>(DEFAULT_CONFIG);
  /** Phone-width sheet height state -- meaningless on desktop/tablet, which is always docked
   *  full-height regardless of this. Reset to the active leaf's `startFullHeight` whenever the
   *  leaf's config actually changes (see updateConfigFromRoute), but otherwise driven purely by
   *  the drag gesture, so navigating within the same leaf's own sub-routes (e.g. the
   *  interaction screen's outcome tiles -> its 'objection' sibling route, both
   *  INTERACTION_SHEET) doesn't snap a manually-expanded sheet back down. */
  mobileState = signal<MobileSheetState>('peek');
  dragOffset = signal(0);
  isDragging = signal(false);

  private activeHost: ActionSheetHost | null = null;
  private dragStartY = 0;
  private routerEventsSub: Subscription;

  /** Drag distance (px) past which a release commits to the next state -- dismiss from peek,
   *  peek<->full otherwise -- rather than snapping back to the current one. */
  private static readonly DRAG_THRESHOLD_PX = 80;

  constructor() {
    this.routerEventsSub = this.router.events.subscribe((e) => {
      if (e instanceof NavigationEnd) this.updateConfigFromRoute();
    });
    this.updateConfigFromRoute();
  }

  ngOnDestroy(): void {
    this.routerEventsSub.unsubscribe();
  }

  /** The active leaf's `data.actionSheet` is the deepest one set while descending this route's
   *  own child chain -- every /map/addresses/** leaf sets it directly on its own route. */
  private updateConfigFromRoute(): void {
    let snapshot: ActivatedRouteSnapshot = this.route.snapshot;
    let config = (snapshot.data['actionSheet'] as ActionSheetConfig | undefined) ?? DEFAULT_CONFIG;
    while (snapshot.firstChild) {
      snapshot = snapshot.firstChild;
      config = (snapshot.data['actionSheet'] as ActionSheetConfig | undefined) ?? config;
    }
    if (config !== this.config()) {
      this.mobileState.set(config.startFullHeight ? 'full' : 'peek');
    }
    this.config.set(config);
  }

  onOutletActivate(component: unknown): void {
    this.activeHost = hasCanDismiss(component) ? component : null;
  }

  onOutletDeactivate(): void {
    this.activeHost = null;
  }

  private canDismiss(): boolean {
    return this.activeHost?.canDismissActionSheet() ?? true;
  }

  onClickOutside(): void {
    if (!this.canDismiss()) return;
    this.router.navigate(['/map']);
  }

  onDragStart(event: PointerEvent): void {
    this.isDragging.set(true);
    this.dragStartY = event.clientY;
    (event.target as HTMLElement).setPointerCapture(event.pointerId);
  }

  onDragMove(event: PointerEvent): void {
    if (!this.isDragging()) return;
    const delta = event.clientY - this.dragStartY;
    // At full height there's nowhere further up to go -- only follow the pointer downward
    // (toward collapsing to peek). At peek, follow both directions: up toward full, down
    // toward dismiss.
    this.dragOffset.set(this.mobileState() === 'full' ? Math.max(0, delta) : delta);
  }

  onDragEnd(): void {
    if (!this.isDragging()) return;
    this.isDragging.set(false);
    const offset = this.dragOffset();
    this.dragOffset.set(0);

    if (this.mobileState() === 'full') {
      if (offset > ActionSheetComponent.DRAG_THRESHOLD_PX) this.mobileState.set('peek');
      return;
    }
    if (offset > ActionSheetComponent.DRAG_THRESHOLD_PX) {
      if (this.canDismiss()) this.router.navigate(['/map']);
    } else if (offset < -ActionSheetComponent.DRAG_THRESHOLD_PX) {
      this.mobileState.set('full');
    }
  }
}
